<template>
  <section class="management-analytics">
    <div class="management-analytics__header">
      <h2 class="management-analytics__title">
        Управленческий потенциал и ЭИ ресурс компании
      </h2>

      <div class="management-analytics__filters">
        <a-select
            v-model:value="selectedGroup"
            :options="groupOptions"
            class="management-analytics__select"
            :disabled="!hasSubscription"
        />

        <div>
          <a-range-picker
              v-model:value="selectedPeriod"
              :placeholder="['Дата начала', 'Дата конца']"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              separator="-"
              inputReadOnly
              class="management-analytics__date compact-range-picker"
              :disabled="!hasSubscription"
          />
        </div>
      </div>
    </div>

    <div class="management-analytics-grid">
      <SmallCardWrapper
          name="Управленческий потенциал"
          :helpText="'Управленческий потенциал'"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="managementSubscriptionText"
          />
        </div>

        <ManagementDistributionCard
            :levels="managementPotential.items"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>

      <template v-if="!managementScoresAreLocked">
        <SmallCardWrapper
            name="Зеленые и красные флаги"
            :helpText="'Зеленые и красные флаги'"
            :loading="dataIsLoading"
            :locked="managementScoresAreLocked"
            :showLockedContent="true"
            :showSubscriptionInfo="false"
            :heightType="managementScoresAreLocked ? 'empty' : ''"
            :greyHeader="managementScoresAreLocked"
        >
          <div v-if="!hasSubscription" class="subsription-locked">
            <SubscriptionRequiredWarning
                @showOrder="showOrderForm"
                :customText="managementSubscriptionText"
            />
          </div>

          <FlagsDetailedCard
              :groups="flagsDetailed.groups"
              :segments="flagsDetailed.segments"
              :class="{ locked: !hasSubscription || managementScoresAreLocked }"
          />
        </SmallCardWrapper>

        <SmallCardWrapper
            name="ЭИ ресурс компании"
            :helpText="'ЭИ ресурс компании'"
            :loading="dataIsLoading"
            :locked="managementScoresAreLocked"
            :showLockedContent="true"
            :showSubscriptionInfo="false"
            :heightType="managementScoresAreLocked ? 'empty' : ''"
            :greyHeader="managementScoresAreLocked"
        >
          <div v-if="!hasSubscription" class="subsription-locked">
            <SubscriptionRequiredWarning
                @showOrder="showOrderForm"
                :customText="managementSubscriptionText"
            />
          </div>

          <ManagementDistributionCard
              :score="resource.score"
              :change="resource.change"
              :changeLabel="resource.changeLabel"
              :indexes="resource.indexes"
              :class="{ locked: !hasSubscription || managementScoresAreLocked }"
          />
        </SmallCardWrapper>
      </template>

      <SmallCardWrapper
          name="Соотношение доменов"
          :helpText="'Соотношение доменов'"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="managementSubscriptionText"
          />
        </div>

        <div class="analytics-card analytics-card--domains">
          <DomainsDonutChart
              :items="domains"
              :class="{ locked: !hasSubscription }"
          />

          <div
              class="domain-card__legend"
              :class="{ locked: !hasSubscription }"
          >
            <div
                v-for="item in normalizedDomains"
                :key="item.key"
                class="domain-card__legend-row"
            >
              <span
                  class="analytics-card__mark"
                  :style="{ backgroundColor: item.color }"
              ></span>

              <span>
                {{ item.label }} —
                <span
                    class="domain-card__legend-value"
                    :class="{
                      'domain-card__legend-value-underlined': item.value
                    }"
                >
                  {{ item.value }} чел.
                </span>
              </span>
            </div>
          </div>
        </div>
      </SmallCardWrapper>

      <template v-if="!managementScoresAreLocked">
        <SmallCardWrapper
            name="Зеленые и красные флаги"
            :helpText="'Зеленые и красные флаги'"
            :loading="dataIsLoading"
            :showLockedContent="true"
        >
          <div v-if="!hasSubscription" class="subsription-locked">
            <SubscriptionRequiredWarning
                @showOrder="showOrderForm"
                :customText="managementSubscriptionText"
            />
          </div>
          <ManagementDistributionCard
              :levels="flagsSimple.items"
              :class="{ locked: !hasSubscription }"
          />
        </SmallCardWrapper>
      </template>

      <template v-else>
        <SmallCardWrapper
            name="Зеленые и красные флаги"
            :helpText="'Зеленые и красные флаги'"
            :loading="dataIsLoading"
            :showLockedContent="true"
        >
          <ManagementDistributionCard
              :levels="flagsSimple.items"
          />
        </SmallCardWrapper>
      </template>
    </div>

    <div class="ei-profile-grid">
      <SmallCardWrapper
          name="Средний профиль ЭИ"
          :helpText="'Средний профиль ЭИ'"
          :loading="dataIsLoading"
      >

        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="managementSubscriptionText"
          />
        </div>

        <EiScaleRowsTable
            :sections="averageProfileSections"
            :showScaleAfterEachSection="true"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Детализация ЭИ (10 разделов способностей)"
          :helpText="'Детализация ЭИ (10 разделов способностей)'"
          :loading="dataIsLoading"
          :locked="managementScoresAreLocked"
          :showLockedContent="false"
          :showSubscriptionInfo="false"
          :heightType="managementScoresAreLocked ? 'empty' : ''"
          :greyHeader="managementScoresAreLocked"
          :class="{ 'ei-profile-grid__item--locked': managementScoresAreLocked }"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="managementSubscriptionText"
          />
        </div>
        <EiScaleRowsTable
            :sections="detailsSections"
            :showScaleAfterEachSection="false"
            :showFirstSectionTitle="true"
            class="ei-profile-details-table"
            :class="{ locked: !hasSubscription || managementScoresAreLocked }"
        />
      </SmallCardWrapper>
    </div>

    <div
        v-if="hasSubscription && managementScoresAreLocked"
        class="management-analytics-grid management-analytics-grid--bottom"
    >
      <SmallCardWrapper
          name="Зеленые и красные флаги"
          :helpText="'Зеленые и красные флаги'"
          :loading="dataIsLoading"
          :locked="hasSubscription"
          :showLockedContent="false"
          :showSubscriptionInfo="false"
          :heightType="'empty'"
          :greyHeader="true"
      >
        <FlagsDetailedCard
            :groups="flagsDetailed.groups"
            :segments="flagsDetailed.segments"
            class="locked"
        />
      </SmallCardWrapper>

      <SmallCardWrapper
          name="ЭИ ресурс компании"
          :helpText="'ЭИ ресурс компании'"
          :loading="dataIsLoading"
          :locked="hasSubscription"
          :showLockedContent="false"
          :showSubscriptionInfo="false"
          :heightType="'empty'"
          :greyHeader="true"
      >
        <ManagementDistributionCard
            :score="resource.score"
            :change="resource.change"
            :changeLabel="resource.changeLabel"
            :indexes="resource.indexes"
            class="locked"
        />
      </SmallCardWrapper>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import dayjs from 'dayjs'

import SmallCardWrapper from '@/components/Widgets/WidgetCard.vue'
import SubscriptionRequiredWarning from '@/components/OtherElements/SubscriptionRequiredWarning.vue'
import EiScaleRowsTable from '@/components/Analytics/EiScaleRowsTable.vue'
import DomainsDonutChart from '@/components/Analytics/DomainsDonutChart.vue'
import ManagementDistributionCard from '@/components/Analytics/ManagementDistributionCard.vue'
import FlagsDetailedCard from '@/components/Analytics/FlagsDetailedCard.vue'
import { formatMonitoringDateForPicker, getMonitoringDateSetting, setMonitoringDateSetting } from '@/utils/monitoringDateStorage'

const EI_PROFILE_CARDS = new Map([
  [
    'total',
    {
      title: 'Общий ЭИ',
      descriptionContent: 'Эмоциональный интеллект - это четко определяемая и измеряемая когнитивная способность перерабатывать информацию, содержащуюся в эмоциях, определять значение эмоций, их связи друг с другом, использовать эмоциональную информацию в качестве основы для мышления и принятия решений.',
      domain: '',
      branch: '',
      graphic_order: 0,
    },
  ],
  [
    'domain_1',
    {
      title: 'Опытный домен',
      descriptionContent: 'Опытный эмоциональный интеллект - это способность воспринимать эмоциональную информацию, отвечать на нее и распоряжаться ею, не обязательно при этом понимая ее.',
      domain: 'Опытный домен',
      branch: '',
      graphic_order: 1,
    },
  ],
  [
    'domain_2',
    {
      title: 'Стратегический домен',
      descriptionContent: 'Стратегический эмоциональный интеллект - это способность понимать эмоциональную информацию и использовать ее стратегически – для планирования и саморегуляции.',
      domain: 'Стратегический домен',
      branch: '',
      graphic_order: 2,
    },
  ],
  [
    'branch_1',
    {
      title: 'Распознавание эмоций',
      descriptionContent: 'Распознавание эмоций – это способность распознавать свои эмоции и эмоции окружающих через речь, мимику, внешний вид, физические проявления эмоций.',
      domain: 'Опытный домен',
      branch: '',
      graphic_order: 3,
    },
  ],
  [
    'branch_2',
    {
      title: 'Использование эмоций для решения задач',
      descriptionContent: 'Использование эмоций для решения задач – это способность грамотно использовать определенные эмоции в качестве основы для принятия решений.',
      domain: 'Опытный домен',
      branch: '',
      graphic_order: 4,
    },
  ],
  [
    'branch_3',
    {
      title: 'Анализ эмоций',
      descriptionContent: 'Анализ эмоций – это способность понимать и анализировать сложные эмоции и чувства, распознавать причины эмоций, а также прогнозировать последствия эмоций.',
      domain: 'Стратегический домен',
      branch: '',
      graphic_order: 5,
    },
  ],
  [
    'branch_4',
    {
      title: 'Управление эмоциями',
      descriptionContent: 'Управление эмоциями – это способность регулировать свои эмоции и влиять на эмоции других людей.',
      domain: 'Стратегический домен',
      branch: '',
      graphic_order: 6,
    },
  ],
  [
    'section_1',
    {
      title: 'Распознавание эмоций в лицах',
      descriptionContent: 'Раздел, направленный на эмоциональное оценивание лиц.',
      domain: 'Опытный домен',
      branch: 'Распознавание эмоций',
      graphic_order: 7,
    },
  ],
  [
    'section_2',
    {
      title: 'Фасилитация/применение эмоций в действиях',
      descriptionContent: 'Раздел, направленный на измерение способности к определению эмоций, эффективных для содействия мышлению в конкретной деятельности.',
      domain: 'Опытный домен',
      branch: 'Использование эмоций для решения задач',
      graphic_order: 10,
    },
  ],
  [
    'section_3',
    {
      title: 'Анализ изменений эмоций',
      descriptionContent: 'Раздел, направленный на измерение способности человека понимать особенности перехода эмоций друг в друга.',
      domain: 'Стратегический домен',
      branch: 'Анализ эмоций',
      graphic_order: 12,
    },
  ],
  [
    'section_4',
    {
      title: 'Регуляция своих эмоций',
      descriptionContent: 'Раздел, направленный на изучение способности человека регулировать собственные эмоциональные состояния.',
      domain: 'Стратегический домен',
      branch: 'Управление эмоциями',
      graphic_order: 15,
    },
  ],
  [
    'section_5',
    {
      title: 'Распознавание эмоций в среде',
      descriptionContent: 'Раздел, направленный на измерение способности человека определять эмоции в окружающем пространстве, среде, произведениях искусства.',
      domain: 'Опытный домен',
      branch: 'Распознавание эмоций',
      graphic_order: 8,
    },
  ],
  [
    'section_6',
    {
      title: 'Эмпатия',
      descriptionContent: 'Раздел, направленный на измерение способности человека описывать свои эмоциональные состояния и почувствовать эмоции другого.',
      domain: 'Опытный домен',
      branch: 'Использование эмоций для решения задач',
      graphic_order: 11,
    },
  ],
  [
    'section_7',
    {
      title: 'Анализ соединения эмоций в чувства',
      descriptionContent: 'Раздел, направленный на диагностику способности человека различать эмоции, входящие в комплексные чувства.',
      domain: 'Стратегический домен',
      branch: 'Анализ эмоций',
      graphic_order: 13,
    },
  ],
  [
    'section_8',
    {
      title: 'Влияние на эмоции других',
      descriptionContent: 'Раздел, направленный на измерение способности человека регулировать эмоциональные состояния других людей.',
      domain: 'Стратегический домен',
      branch: 'Управление эмоциями',
      graphic_order: 16,
    },
  ],
  [
    'section_9a',
    {
      title: 'Распознавание эмоций в контексте ситуаций',
      descriptionContent: 'Раздел, направленный на оценку способности человека распознавать эмоции в динамике и с учетом дополнительных факторов.',
      domain: 'Опытный домен',
      branch: 'Распознавание эмоций',
      graphic_order: 9,
    },
  ],
  [
    'section_9b',
    {
      title: 'Анализ динамики эмоций в контексте ситуаций',
      descriptionContent: 'Раздел, направленный на измерение способности оценивать динамику эмоциональных состояний с учетом внешних факторов.',
      domain: 'Стратегический домен',
      branch: 'Анализ эмоций',
      graphic_order: 14,
    },
  ],
])

export default {
  name: 'ManagementAnalyticsSection',

  props: {
    hasSubscription: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    SmallCardWrapper,
    SubscriptionRequiredWarning,
    EiScaleRowsTable,
    DomainsDonutChart,
    ManagementDistributionCard,
    FlagsDetailedCard,
  },

  setup(props, context) {
    const MANAGEMENT_POTENTIAL_ORDER = [
      'LEVEL_POTENTIAL_OVER_HIGH',
      'LEVEL_POTENTIAL_HIGHEST',
      'LEVEL_POTENTIAL_MODERATE',
      'LEVEL_POTENTIAL_MODERATE_RISKS',
      'LEVEL_POTENTIAL_LOW',
    ]

    const MANAGEMENT_POTENTIAL_COLORS = {
      LEVEL_POTENTIAL_OVER_HIGH: '#21AC0C',
      LEVEL_POTENTIAL_HIGHEST: '#8BD05B',
      LEVEL_POTENTIAL_MODERATE: '#DDE00F',
      LEVEL_POTENTIAL_MODERATE_RISKS: '#FF9F40',
      LEVEL_POTENTIAL_LOW: '#F55565',
    }

    const EI_RESOURCE_ORDER = ['highest', 'high', 'medium', 'low']

    const EI_RESOURCE_LABELS = {
      highest: 'Высший',
      high: 'Высокий',
      medium: 'Средний',
      low: 'Низкий',
    }

    const EI_RESOURCE_COLORS = {
      highest: '#21AC0C',
      high: '#8BD05B',
      medium: '#FF9F40',
      low: '#F55565',
    }

    const DOMAIN_LABELS = {
      drivers: 'Драйверы',
      strategists: 'Стратеги',
      balanced: 'Сбалансированные',
    }

    const DOMAIN_COLORS = {
      drivers: '#A29BFE',
      strategists: '#71E5E0',
      balanced: '#D5DBE2',
    }

    const EI_PROFILE_GREEN_COLOR = '#99DA6E'
    const EI_PROFILE_RED_COLOR = '#F55565'
    const EI_PROFILE_GREY_COLOR = '#D5DBE2'

    const AVERAGE_PROFILE_STRUCTURE = [
      {
        key: 'general',
        title: null,
        keys: ['total'],
      },
      {
        key: 'domains',
        title: 'Домены',
        keys: ['domain_1', 'domain_2'],
      },
      {
        key: 'branches',
        title: 'Ветви',
        keys: ['branch_1', 'branch_2', 'branch_3', 'branch_4'],
      },
    ];

    const DETAIL_PROFILE_KEYS_IN_CURRENT_UI_ORDER = [
      'section_1',
      'section_5',
      'section_9a',
      'section_2',
      'section_6',
      'section_3',
      'section_7',
      'section_9b',
      'section_4',
      'section_8',
    ]

    const TEMP_RESOURCE_SCORE = 112
    const TEMP_RESOURCE_PREVIOUS_SCORE = 104

    const DEFAULT_EI_PROFILE_VALUES = {
      total: 108,
      domain_1: 124,
      domain_2: 92,
      branch_1: 118,
      branch_2: 101,
      branch_3: 130,
      branch_4: 86,
      section_1: 128,
      section_2: 121,
      section_3: 97,
      section_4: 103,
      section_5: 116,
      section_6: 84,
      section_7: 130,
      section_8: 112,
      section_9a: 109,
      section_9b: 88,
    }

    const DEFAULT_MANAGEMENT_POTENTIAL = {
      items: [
        { key: 'LEVEL_POTENTIAL_OVER_HIGH', label: 'Максимальный', value: 13, color: '#21AC0C' },
        { key: 'LEVEL_POTENTIAL_HIGHEST', label: 'Высокий', value: 18, color: '#8BD05B' },
        { key: 'LEVEL_POTENTIAL_MODERATE', label: 'Средний', value: 350, color: '#DDE00F' },
        { key: 'LEVEL_POTENTIAL_MODERATE_RISKS', label: 'Умеренный', value: 12, color: '#FF9F40' },
        { key: 'LEVEL_POTENTIAL_LOW', label: 'Низкий', value: 5, color: '#F55565' },
      ],
    }

    const DEFAULT_FLAGS_DETAILED = {
      groups: [
        {
          key: 'balanced',
          title: 'Сотрудники с балансом ЭИ',
          total: 20,
          color: '#21AC0C',
          items: [
            { key: 'highest_balanced', label: 'В высоких границах, > 120', value: 13 },
            { key: 'high_balanced', label: 'В средних границах, 100–120', value: 7 },
          ],
        },
        {
          key: 'risks',
          title: 'Сотрудники с рисками',
          total: 81,
          color: '#F55565',
          items: [
            { key: 'burnout_risk', label: 'Выгорание', value: 21 },
            { key: 'manipulative_risk', label: 'Манипулятивность', value: 13 },
            { key: 'led_risk', label: 'Не способен влиять на эмоции других', value: 35 },
            { key: 'impulsivity_risk', label: 'Риск импульсивности', value: 12 },
          ],
        },
        {
          key: 'moderate',
          title: 'Остальные',
          total: 100,
          color: '#D5DBE2',
          items: [],
        },
      ],
      segments: [
        { key: 'balanced', value: 20, color: '#21AC0C' },
        { key: 'moderate', value: 100, color: '#D5DBE2' },
        { key: 'risks', value: 81, color: '#F55565' },
      ],
    }

    const DEFAULT_RESOURCE = {
      score: TEMP_RESOURCE_SCORE,
      previousScore: TEMP_RESOURCE_PREVIOUS_SCORE,
      change: Math.round(((TEMP_RESOURCE_SCORE - TEMP_RESOURCE_PREVIOUS_SCORE) / TEMP_RESOURCE_PREVIOUS_SCORE) * 100),
      changeLabel: 'за период',
      indexes: [
        { key: 'highest', label: EI_RESOURCE_LABELS.highest, value: 13, color: EI_RESOURCE_COLORS.highest },
        { key: 'high', label: EI_RESOURCE_LABELS.high, value: 18, color: EI_RESOURCE_COLORS.high },
        { key: 'medium', label: EI_RESOURCE_LABELS.medium, value: 38, color: EI_RESOURCE_COLORS.medium },
        { key: 'low', label: EI_RESOURCE_LABELS.low, value: 9, color: EI_RESOURCE_COLORS.low },
      ],
    }

    const DEFAULT_DOMAINS = [
      { key: 'drivers', label: 'Драйверы', value: 64, color: '#9B8CFF' },
      { key: 'strategists', label: 'Стратеги', value: 25, color: '#67DCE0' },
      { key: 'balanced', label: 'Сбалансированные', value: 6, color: '#D5DBE2' },
    ]

    const DEFAULT_FLAGS_SIMPLE = {
      items: [
        { key: 'green', label: 'ЭИ > 120', value: 13, color: '#21AC0C' },
        { key: 'other', label: 'ЭИ 80 — 120', value: 46, color: '#D5DBE2' },
        { key: 'red', label: 'ЭИ < 80', value: 12, color: '#F55565' },
      ],
    }

    const store = useStore();

    const groups = computed(() => store.state.group.groups ?? []);
    const managementLevels = computed(() => store.state.analytics.monitoringData?.managementLevels ?? null);

    const dateStorageKey = 'monitoring:management-analytics:date'
    const defaultSelectedPeriod = [
      dayjs().subtract(6, 'month').format('YYYY-MM-DD'),
      dayjs().format('YYYY-MM-DD'),
    ]
    const savedSelectedPeriod = getMonitoringDateSetting(dateStorageKey, defaultSelectedPeriod)
    const pickerSelectedPeriod = Array.isArray(savedSelectedPeriod)
        ? savedSelectedPeriod.map(formatMonitoringDateForPicker)
        : savedSelectedPeriod
    const savedSelectedPeriodIsValid = Array.isArray(pickerSelectedPeriod) && pickerSelectedPeriod.length === 2
    const customDateDataIsReady = ref(!savedSelectedPeriodIsValid)

    const selectedGroup = ref('all');
    const selectedPeriod = ref(savedSelectedPeriodIsValid ? pickerSelectedPeriod : defaultSelectedPeriod);
    const dataIsLoading = ref(false);
    const subscriptionHasManagementScores = ref(true);
    const managementSubscriptionText = '';

    const hasSubscriptionAccess = computed(() => props.hasSubscription);
    const managementScoresAreLocked = computed(() => {
      return hasSubscriptionAccess.value && !subscriptionHasManagementScores.value
    });

    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const toRoundedNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? Math.round(number) : 0
    }

    const getPercentChange = (currentValue, previousValue) => {
      const current = Number(currentValue)
      const previous = Number(previousValue)

      if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
        return 0
      }

      return Math.round(((current - previous) / previous) * 100)
    }

    const getAverageProfileBackendField = (cardKey) => {
      return `company_${cardKey}`;
    }

    const getDetailProfileBackendField = (cardKey) => {
      return `company_${cardKey}`;
    }

    const getRankedProfileRowKeys = (rows, direction, limit, excludedKeys = new Set()) => {
      return new Set(
          rows
              .map((row, index) => ({ row, index }))
              .filter(({ row }) => row.value > 0 && !excludedKeys.has(row.key))
              .sort((a, b) => {
                const valueDiff = direction === 'highest'
                    ? b.row.value - a.row.value
                    : a.row.value - b.row.value

                return valueDiff || a.index - b.index
              })
              .slice(0, limit)
              .map(({ row }) => row.key)
      )
    }

    const applyRankedProfileColors = (rows, highestLimit, lowestLimit) => {
      const highestKeys = getRankedProfileRowKeys(rows, 'highest', highestLimit)
      const lowestKeys = getRankedProfileRowKeys(rows, 'lowest', lowestLimit, highestKeys)

      return rows.map((row) => ({
        ...row,
        color: highestKeys.has(row.key)
            ? EI_PROFILE_GREEN_COLOR
            : lowestKeys.has(row.key)
                ? EI_PROFILE_RED_COLOR
                : EI_PROFILE_GREY_COLOR,
      }))
    }

    const applyAverageProfileColors = (sections) => {
      return sections.map((section) => {
        if (section.key === 'domains' || section.key === 'branches') {
          return {
            ...section,
            rows: applyRankedProfileColors(section.rows, 1, 1),
          }
        }

        return {
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            color: EI_PROFILE_GREY_COLOR,
          })),
        }
      })
    }

    const applyDetailsProfileColors = (sections) => {
      return sections.map((section) => ({
        ...section,
        rows: applyRankedProfileColors(section.rows, 2, 2),
      }))
    }

    const buildEiProfileRow = ({
                                  cardKey,
                                 source,
                                 backendField,
                                 fallbackToZero = true,
                                 defaultValue = 110,
                               }) => {
      const card = EI_PROFILE_CARDS.get(cardKey)
      const rawValue = source?.[backendField]

      return {
        key: cardKey,
        label: card?.title || cardKey,
        value: rawValue != null
            ? toRoundedNumber(rawValue)
            : fallbackToZero
                ? 0
                : defaultValue,
        color: EI_PROFILE_GREY_COLOR,
        helpText: card?.descriptionContent || '',
      }
    }

    const buildZeroAverageProfileSections = () => {
      return AVERAGE_PROFILE_STRUCTURE.map((section) => ({
        key: section.key,
        title: section.title,
        rows: section.keys.map((cardKey) => {
          const card = EI_PROFILE_CARDS.get(cardKey)

          return {
            key: cardKey,
            label: card?.title || cardKey,
            value: 0,
            color: EI_PROFILE_GREY_COLOR,
            helpText: card?.descriptionContent || '',
          }
        }),
      }))
    }

    const buildZeroDetailsSections = () => {
      return [
        {
          key: 'sections',
          title: 'Разделы',
          rows: DETAIL_PROFILE_KEYS_IN_CURRENT_UI_ORDER.map((cardKey) => {
            const card = EI_PROFILE_CARDS.get(cardKey)

            return {
              key: cardKey,
              label: card?.title || cardKey,
              value: 0,
              color: EI_PROFILE_GREY_COLOR,
              helpText: card?.descriptionContent || '',
            }
          }),
        },
      ]
    }

    const buildDefaultAverageProfileSections = () => {
      return applyAverageProfileColors(AVERAGE_PROFILE_STRUCTURE.map((section) => ({
        key: section.key,
        title: section.title,
        rows: section.keys.map((cardKey) => {
          return buildEiProfileRow({
            cardKey,
            source: {},
            backendField: getAverageProfileBackendField(cardKey),
            fallbackToZero: false,
            defaultValue: DEFAULT_EI_PROFILE_VALUES[cardKey] ?? 110,
          })
        }),
      })))
    }

    const buildDefaultDetailsSections = () => {
      return applyDetailsProfileColors([
        {
          key: 'sections',
          title: '',
          rows: DETAIL_PROFILE_KEYS_IN_CURRENT_UI_ORDER.map((cardKey) => {
            return buildEiProfileRow({
              cardKey,
              source: {},
              backendField: getDetailProfileBackendField(cardKey),
              fallbackToZero: false,
              defaultValue: DEFAULT_EI_PROFILE_VALUES[cardKey] ?? 110,
            })
          }),
        },
      ])
    }

    const groupOptions = computed(() => {
      return [
        {
          label: 'Все сотрудники',
          value: 'all',
        },
        ...groups.value.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ]
    })

    const periodInterval = computed(() => {
      if (
          !Array.isArray(selectedPeriod.value) ||
          selectedPeriod.value.length !== 2 ||
          !selectedPeriod.value[0] ||
          !selectedPeriod.value[1]
      ) {
        return null
      }

      const [from, to] = selectedPeriod.value

      return [
        dayjs(from).format('DD.MM.YYYY'),
        dayjs(to).format('DD.MM.YYYY'),
      ].join('-')
    })

    const backendData = computed(() => {
      return customDateDataIsReady.value ? managementLevels.value : null
    })

    const buildManagementPotential = (data) => {
      const list = Array.isArray(data?.management_potential_score)
          ? data.management_potential_score
          : []

      return {
        items: MANAGEMENT_POTENTIAL_ORDER.map((type) => {
          const source = list.find((item) => item.type === type)

          return {
            key: type,
            label: source?.label || '',
            value: toNumber(source?.count),
            color: MANAGEMENT_POTENTIAL_COLORS[type] || '#D5DBE2',
          }
        }),
      }
    }

    const buildFlagsDetailed = (data) => {
      const balance = data?.detailed_flags?.group_balance || {}
      const risks = data?.detailed_flags?.risks || {}
      const moderateProfiles = toNumber(data?.detailed_flags?.moderate_profiles)

      const balancedItems = [
        {
          key: 'highest_balanced',
          label: 'В высоких границах, > 120',
          value: toNumber(balance.highest_balanced),
        },
        {
          key: 'high_balanced',
          label: 'В средних границах, 100–120',
          value: toNumber(balance.high_balanced),
        },
      ]

      const riskItems = [
        {
          key: 'burnout_risk',
          label: 'Выгорание',
          value: toNumber(risks.burnout_risk),
        },
        {
          key: 'manipulative_risk',
          label: 'Манипулятивность',
          value: toNumber(risks.manipulative_risk),
        },
        {
          key: 'led_risk',
          label: 'Не способен влиять на эмоции других',
          value: toNumber(risks.led_risk),
        },
        {
          key: 'impulsivity_risk',
          label: 'Риск импульсивности',
          value: toNumber(risks.impulsivity_risk),
        },
      ]

      const balancedTotal = balancedItems.reduce((sum, item) => sum + item.value, 0)
      const risksTotal = riskItems.reduce((sum, item) => sum + item.value, 0)

      return {
        groups: [
          {
            key: 'balanced',
            title: 'Сотрудники с балансом ЭИ',
            total: balancedTotal,
            color: '#21AC0C',
            items: balancedItems,
          },
          {
            key: 'risks',
            title: 'Сотрудники с рисками',
            total: risksTotal,
            color: '#F55565',
            items: riskItems,
          },
          {
            key: 'moderate',
            title: 'Остальные',
            total: moderateProfiles,
            color: '#D5DBE2',
            items: [],
          },
        ],
        segments: [
          {
            key: 'balanced',
            value: balancedTotal,
            color: '#21AC0C',
          },
          {
            key: 'moderate',
            value: moderateProfiles,
            color: '#D5DBE2',
          },
          {
            key: 'risks',
            value: risksTotal,
            color: '#F55565',
          },
        ],
      }
    }

    const buildResource = (data) => {
      const source = data?.ei_resources_score || {}

      const indexes = EI_RESOURCE_ORDER.map((key) => ({
        key,
        label: EI_RESOURCE_LABELS[key],
        value: toNumber(source[key]),
        color: EI_RESOURCE_COLORS[key],
      }))

      const nonZeroValues = indexes.find((el) => el.value);

      const score = nonZeroValues ? TEMP_RESOURCE_SCORE : 0;
      const previousScore = nonZeroValues ? TEMP_RESOURCE_PREVIOUS_SCORE : 0;
      const change = nonZeroValues ? getPercentChange(score, previousScore) : 0;

      return {
        score,
        previousScore,
        change,
        changeLabel: 'за период',
        indexes,
      }
    }

    const buildDomains = (data) => {
      const source = data?.domain_ratio || {}

      return Object.keys(source).map((key, index) => ({
        key,
        label: DOMAIN_LABELS[key] || key,
        value: toNumber(source[key]),
        color:
            DOMAIN_COLORS[key] ||
            ['#A29BFE', '#71E5E0', '#D5DBE2'][index % 5],
      }))
    }

    const buildFlagsSimple = (data) => {
      const source = data?.flags_by_employee_count || {}

      return {
        items: [
          {
            key: 'green',
            label: 'ЭИ > 120',
            value: toNumber(source.green),
            color: '#21AC0C',
          },
          {
            key: 'other',
            label: 'ЭИ 80 — 120',
            value: toNumber(source.other),
            color: '#D5DBE2',
          },
          {
            key: 'red',
            label: 'ЭИ < 80',
            value: toNumber(source.red),
            color: '#F55565',
          },
        ],
      }
    }

    const buildAverageProfileSections = (data) => {
      const source = data?.average_ei_profile || {}

      return applyAverageProfileColors(AVERAGE_PROFILE_STRUCTURE.map((section) => ({
        key: section.key,
        title: section.title,
        rows: section.keys.map((cardKey) => {
          return buildEiProfileRow({
            cardKey,
            source,
            backendField: getAverageProfileBackendField(cardKey),
            fallbackToZero: true,
          })
        }),
      })))
    }

    const buildDetailsSections = (data) => {
      const source = data?.ei_abilities_detail || {}

      return applyDetailsProfileColors([
        {
          key: 'sections',
          title: 'Разделы',
          rows: DETAIL_PROFILE_KEYS_IN_CURRENT_UI_ORDER.map((cardKey) => {
            return buildEiProfileRow({
              cardKey,
              source,
              backendField: getDetailProfileBackendField(cardKey),
              fallbackToZero: true,
            })
          }),
        },
      ])
    }

    const managementPotential = computed(() => {
      if (!hasSubscriptionAccess.value) {
        return {
          items: DEFAULT_MANAGEMENT_POTENTIAL.items.map((item) => ({ ...item })),
        }
      }

      return buildManagementPotential(backendData.value)
    })

    const flagsDetailed = computed(() => {
      if (!hasSubscriptionAccess.value) {
        return {
          groups: DEFAULT_FLAGS_DETAILED.groups.map((group) => ({
            ...group,
            items: group.items.map((item) => ({ ...item })),
          })),
          segments: DEFAULT_FLAGS_DETAILED.segments.map((item) => ({ ...item })),
        }
      }

      return buildFlagsDetailed(backendData.value)
    })

    const resource = computed(() => {
      if (!hasSubscriptionAccess.value) {
        return {
          ...DEFAULT_RESOURCE,
          indexes: DEFAULT_RESOURCE.indexes.map((item) => ({ ...item })),
        }
      }

      return buildResource(backendData.value)
    })

    const domains = computed(() => {
      if (!hasSubscriptionAccess.value) {
        return DEFAULT_DOMAINS.map((item) => ({ ...item }))
      }

      return buildDomains(backendData.value)
    })

    const normalizedDomains = computed(() => {
      return domains.value.map((item) => ({
        ...item,
        value: toNumber(item.value),
      }))
    })

    const hasDomainsData = computed(() => {
      return normalizedDomains.value.some((item) => item.value > 0)
    })

    const flagsSimple = computed(() => {
      if (!hasSubscriptionAccess.value) {
        return {
          items: DEFAULT_FLAGS_SIMPLE.items.map((item) => ({ ...item })),
        }
      }

      return buildFlagsSimple(backendData.value)
    })

    const averageProfileSections = computed(() => {
      if (!hasSubscriptionAccess.value) {
        return buildDefaultAverageProfileSections()
      }

      return buildAverageProfileSections(backendData.value)
    })

    const detailsSections = computed(() => {
      if (!hasSubscriptionAccess.value) {
        return buildDefaultDetailsSections()
      }

      return buildDetailsSections(backendData.value)
    })

    const fetchData = () => {
      if (!hasSubscriptionAccess.value || !subscriptionHasManagementScores.value) {
        return
      }

      dataIsLoading.value = true

      store
          .dispatch('analytics/getManagementLevels', {
            group_id: selectedGroup.value === 'all' ? null : selectedGroup.value,
            date: periodInterval.value,
          })
          .finally(() => {
            customDateDataIsReady.value = true
            dataIsLoading.value = false
          })
    }

    const showOrderForm = () => {
      context.emit('showOrderForm')
    }

    watch(
        () => [selectedGroup.value, periodInterval.value],
        () => {
          fetchData()
        },
        { immediate: savedSelectedPeriodIsValid }
    )

    watch(
        hasSubscriptionAccess,
        (hasAccess, hadAccess) => {
          if (hasAccess && !hadAccess) {
            fetchData()
          }
        }
    )

    watch(
        () => selectedPeriod.value,
        () => {
          if (Array.isArray(selectedPeriod.value) && selectedPeriod.value.length === 2 && selectedPeriod.value[0] && selectedPeriod.value[1]) {
            setMonitoringDateSetting(dateStorageKey, selectedPeriod.value)
          }
        }
    )

    return {
      selectedGroup,
      selectedPeriod,
      groupOptions,
      dataIsLoading,
      managementPotential,
      flagsDetailed,
      resource,
      domains,
      normalizedDomains,
      hasDomainsData,
      flagsSimple,
      averageProfileSections,
      detailsSections,
      managementScoresAreLocked,
      managementSubscriptionText,

      showOrderForm,
    }
  },
}
</script>

<style scoped lang="scss">
.management-analytics {
  width: 100%;
  text-align: start;
}

.management-analytics__header {
  margin-bottom: 20px;
}

.management-analytics__title {
  margin: 0 0 20px;
  font-weight: 500;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  @media (max-width: $mobile-breakpoint) {
    padding: 0 20px;
  }
}

.management-analytics__filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.management-analytics__select {
  width: 350px;
}

.management-analytics__date {
  width: 200px;
}

.management-analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.management-analytics-grid :deep(.ant-card-body) {
  padding: 0;
}

.management-analytics-grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

.ei-profile-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  text-align: start;
}

.ei-profile-grid :deep(.ant-card-body) {
  padding: 0;
}

.ei-profile-grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

.ei-profile-grid__item--locked {
  align-self: start;
}

.analytics-card {
  width: 100%;
  min-width: 0;
}

.analytics-card--domains {
  position: relative;
  min-height: 170px;
  display: flex;
  align-items: center;
  gap: 28px;
}

.analytics-card__mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.domain-card__legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.domain-card__legend-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.domain-card__legend-row span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.domain-card__legend-value {
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
}

.domain-card__legend-value-underlined {
  text-decoration: underline;
  cursor: pointer;
}

.domain-card__empty {
  position: absolute;
  top: 68%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  text-align: center;
  white-space: nowrap;
}

.subsription-locked {
  margin-bottom: 16px;
}

.locked {
  opacity: 0.5;
}

.compact-range-picker {
  display: flex;
  align-items: center;
}

:deep(.compact-range-picker .ant-picker-input) {
  flex: 0 1 auto;
}

:deep(.compact-range-picker .ant-picker-input > input) {
  padding: 0;
  font-size: 14px;
  text-align: center;
}

:deep(.compact-range-picker .ant-picker-range-separator) {
  margin: 0;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1;
}

:deep(.compact-range-picker .ant-picker-active-bar) {
  display: none;
}

:deep(.compact-range-picker .ant-picker-clear) {
  display: none;
}

:deep(.compact-range-picker .ant-picker-suffix) {
  margin-left: 6px;
  color: rgba(0, 0, 0, 0.45);
}

:deep(.compact-range-picker .ant-picker-input-placeholder > input) {
  text-align: center;
}

@media (max-width: $tablet-breakpoint) {
  .management-analytics-grid,
  .ei-profile-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .management-analytics__filters {
    padding: 0 20px;
  }

  .management-analytics__select,
  .management-analytics__date {
    width: 100%;
  }

  .analytics-card--domains {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
